import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { CalendarIcon, ChevronDown, Filter, X } from 'lucide-react'
import { useMediaQuery } from '@/hooks/use-media-query'

interface CompactFilterBarProps {
  startDate: string
  endDate: string
  compareStartDate?: string
  compareEndDate?: string
  attendantId?: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  onCompareStartDateChange: (date: string | undefined) => void
  onCompareEndDateChange: (date: string | undefined) => void
  onAttendantIdChange: (id: string | undefined) => void
  onClearComparison?: () => void
  attendants?: Array<{ id: string | number; name: string }>
}

const PRESET_PERIODS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
]

function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start)
  const endDate = new Date(end)
  return `${startDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} - ${endDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`
}

export function CompactFilterBar({
  startDate,
  endDate,
  compareStartDate,
  compareEndDate,
  attendantId,
  onStartDateChange,
  onEndDateChange,
  onCompareStartDateChange,
  onCompareEndDateChange,
  onAttendantIdChange,
  onClearComparison,
  attendants = [],
}: CompactFilterBarProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const [sheetOpen, setSheetOpen] = useState(false)
  const [comparisonOpen, setComparisonOpen] = useState(!!compareStartDate)

  // Local state for custom date inputs - prevents immediate fetch on every keystroke
  const [localStartDate, setLocalStartDate] = useState(startDate)
  const [localEndDate, setLocalEndDate] = useState(endDate)

  // Sync local state when sheet opens (reset to current values)
  useEffect(() => {
    if (sheetOpen) {
      setLocalStartDate(startDate)
      setLocalEndDate(endDate)
    }
  }, [sheetOpen, startDate, endDate])

  const applyPreset = (days: number) => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)

    onEndDateChange(end.toISOString().split('T')[0])
    onStartDateChange(start.toISOString().split('T')[0])
  }

  const enableComparison = () => {
    // Set comparison to previous period
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))

    const compareEnd = new Date(start)
    compareEnd.setDate(compareEnd.getDate() - 1)
    const compareStart = new Date(compareEnd)
    compareStart.setDate(compareStart.getDate() - diffDays)

    onCompareStartDateChange(compareStart.toISOString().split('T')[0])
    onCompareEndDateChange(compareEnd.toISOString().split('T')[0])
    setComparisonOpen(true)
  }

  const handleClearComparison = () => {
    onClearComparison?.()
    setComparisonOpen(false)
  }

  // Apply custom dates and close sheet
  const handleApplyCustomDates = () => {
    onStartDateChange(localStartDate)
    onEndDateChange(localEndDate)
    setSheetOpen(false)
  }

  const hasComparison = compareStartDate && compareEndDate

  // Mobile Filter Content
  const filterContent = (
    <div className="space-y-6">
      {/* Preset Period */}
      <div className="space-y-2">
        <Label>Período</Label>
        <div className="flex flex-col gap-2">
          {PRESET_PERIODS.map((preset) => (
            <Button
              key={preset.days}
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                applyPreset(preset.days)
                if (!isDesktop) setSheetOpen(false)
              }}
            >
              Últimos {preset.days} dias
            </Button>
          ))}
        </div>
      </div>

      {/* Date Range - Uses local state to prevent immediate fetch */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="mobile-startDate">Data Início</Label>
          <div className="relative">
            <Input
              id="mobile-startDate"
              type="date"
              value={localStartDate}
              onChange={(e) => setLocalStartDate(e.target.value)}
              className="pl-10"
            />
            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="mobile-endDate">Data Fim</Label>
          <div className="relative">
            <Input
              id="mobile-endDate"
              type="date"
              value={localEndDate}
              onChange={(e) => setLocalEndDate(e.target.value)}
              className="pl-10"
            />
            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Attendant Filter */}
      {attendants.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="mobile-attendant">Atendente</Label>
          <Select
            value={attendantId || 'all'}
            onValueChange={(v) => onAttendantIdChange(v === 'all' ? undefined : v)}
          >
            <SelectTrigger id="mobile-attendant">
              <SelectValue placeholder="Todos os atendentes" />
            </SelectTrigger>
            <SelectContent>
              {[{ id: 'all', name: 'Todos os atendentes' }, ...attendants.map((a) => ({ id: String(a.id), name: a.name }))].map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Comparison Toggle */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Comparar com período anterior</Label>
          <Button
            variant={hasComparison ? 'default' : 'outline'}
            size="sm"
            onClick={hasComparison ? handleClearComparison : enableComparison}
          >
            {hasComparison ? 'Remover' : 'Ativar'}
          </Button>
        </div>
      </div>

      <Button className="w-full" onClick={handleApplyCustomDates}>
        Aplicar Filtros
      </Button>
    </div>
  )

  // Desktop Compact Layout
  if (isDesktop) {
    return (
      <div className="space-y-3">
        {/* Main Filter Bar */}
        <div className="border border-border bg-muted/30 rounded-md p-3">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Preset Tabs */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Período:</span>
              <Tabs value={undefined} onValueChange={undefined}>
                <TabsList className="h-9">
                  {PRESET_PERIODS.map((preset) => (
                    <TabsTrigger
                      key={preset.days}
                      value={preset.label}
                      onClick={() => applyPreset(preset.days)}
                      className="text-xs px-3"
                    >
                      {preset.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>

            {/* Date Range Display */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-background border border-border text-sm">
              <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{formatDateRange(startDate, endDate)}</span>
            </div>

            {/* Attendant Select */}
            {attendants.length > 0 && (
              <Select
                value={attendantId || 'all'}
                onValueChange={(v) => onAttendantIdChange(v === 'all' ? undefined : v)}
              >
                <SelectTrigger className="w-[200px] h-9">
                  <SelectValue placeholder="Atendente" />
                </SelectTrigger>
                <SelectContent>
                  {[{ id: 'all', name: 'Todos' }, ...attendants.map((a) => ({ id: String(a.id), name: a.name }))].map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Comparison Toggle */}
            <Collapsible open={comparisonOpen} onOpenChange={setComparisonOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant={hasComparison ? 'default' : 'outline'}
                  size="sm"
                  className="h-9"
                  onClick={!hasComparison ? enableComparison : undefined}
                >
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Comparar período
                </Button>
              </CollapsibleTrigger>
            </Collapsible>

            {/* Custom Date Picker (optional - could add a popover here) */}
            <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => setSheetOpen(true)}>
              <Filter className="h-3.5 w-3.5 mr-2" />
              Datas customizadas
            </Button>
          </div>
        </div>

        {/* Comparison Period (Collapsible) */}
        {hasComparison && comparisonOpen && (
          <div className="border border-border bg-muted/20 rounded-md p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Comparando:</span>
                <span className="text-muted-foreground">{formatDateRange(compareStartDate!, compareEndDate!)}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleClearComparison}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Sheet for custom dates (desktop fallback) */}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="right" className="overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Filtros Personalizados</SheetTitle>
              <SheetDescription>Configure o período e atendente</SheetDescription>
            </SheetHeader>
            <div className="mt-6">{filterContent}</div>
          </SheetContent>
        </Sheet>
      </div>
    )
  }

  // Mobile Sheet Drawer
  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" className="w-full">
          <Filter className="h-4 w-4 mr-2" />
          Filtros
          {hasComparison && <span className="ml-2 text-xs text-muted-foreground">(comparando)</span>}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Filtros</SheetTitle>
          <SheetDescription>Selecione o período e atendente</SheetDescription>
        </SheetHeader>
        <div className="mt-6">{filterContent}</div>
      </SheetContent>
    </Sheet>
  )
}
